/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 * @hook        : MyApartmentMainApp
 * @description : Custom hook for managing the state and logic related to the Building system.
 *                This includes local states for selection, searching, modals, and permissions,
 *                as well as dispatching the Redux action to fetch building data.
 *
 * @dependencies:
 * - Redux (for fetching and accessing building data and permissions)
 * - js-cookie (for getting the selected language)
 * - i18next (for translations)
 *
 * @returns {Object} An object containing state and functions for building management.
 */

import React, {useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '../../../Routes/routes';
import { TABLES } from '../../../Apis/Tables';
import TableFunction from "../../../components/table/TableFunction";
import DropDownMenuProfile from "../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../components/DropDown/DropDownMenuItem";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import {
    DetailsIconsOptions, IconMoreVerticalDots
} from "../../../components/buttons/IconButtons";
import {VIEW_TYPE} from "../../../enums/ViewType";
import { SimpleSearchWithId } from '../../../reduxToolkit/Search/SearchApi';

const MyApartmentMainApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [deleteModal, setDeleteModal] = useState(false);
    const [createModal, setCreateModal] = useState(false);
    const [updateModal, setUpdateModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [modified, setModified] = useState(false);
    const [search, setSearch] = useState(false); // Search flag
    const [bodyForExport, setBodyForExport] = useState(''); // Data to export
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const searchId = 'txt_bm_search_my_apartment';
    const option = TABLES.MY_BUILDING;
    const [results, setResults] = useState([]);
    // The list only brings the buildings the logged resident belongs to
    const userId = sessionStorage.getItem('id');
    const elementRef = useRef(null);

    // The card grid takes the remaining viewport height so the pagination stays
    // at the bottom of the page instead of following the last card
    useEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            elementRef.current.style.setProperty('--top-value', `${rect.top}px`);
        }
    }, [results?.content]);


    const getActionsButtons = (item, index) => {
        return <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-${item.id}`} // Unique menu ID for each building
            icon={<IconMoreVerticalDots/>}
        >
            <DropDownMenuItem
                id={"btn_mbm_details_"+ item?.id}
                index={index}
                handleClose={handleClose}
                name={t('details')}
                click={() => {
                    // The id the simple search returns is the building apartment
                    // of the resident, it travels with the detail because the
                    // household members of the unit are searched with it
                    navigate(routes.myApartment.details, {
                        state: {
                            id: item?.id,
                            buildingApartmentId: item?.id,
                            buildingId: item?.buildingId,
                            buildingName: item?.buildingName,
                            apartmentName: item?.name
                        }
                    });
                }}
                icon={<DetailsIconsOptions/>}
            />
        </DropDownMenuProfile>
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, options) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key: options?.searchElement ?? searchElement,
            userId
        });
        setBodyForExport(body);

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            }
        );
    };

    /**
     * Search of SearchSimple, the key is already on the state.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    // Use the TableFunction component to handle the list logic (checkbox selection, pagination, etc.)
    const { t, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose} = TableFunction({
        data: results, // List data
        labelRowsPerPage: 'my_apartments_per_page', // Label for rows per page
        hasContainer: true, // Enable container for the list
        fixedPagination: false, // Disable fixed pagination
        view:VIEW_TYPE.GRID.name,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    return {
        t,
        deleteModal,
        setDeleteModal,
        createModal,
        setCreateModal,
        updateModal,
        setUpdateModal,
        invitationLinkModal,
        setInvitationLinkModal,
        selected,
        setSelected,
        modified,
        setModified,
        isAllChecked,
        setIsAllChecked,
        DEV,
        permission,
        selectedList,
        results,
        setResults,
        searchElement,
        setSearchElement,
        search, setSearch,
        option,
        searchId,
        setSelectedList,
        page,
        rowsPerPage,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        handleSearch,
        handleResetSearch,
        pagination,
        elementRef,
        getActionsButtons
    };
};

export default MyApartmentMainApp;
